import { LinearProgress, Stack, Typography } from "@mui/material"
import PersonIcon from "@mui/icons-material/Person";
import CircleIcon from "@mui/icons-material/Circle";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CallIcon from "@mui/icons-material/Call";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import CallEndIcon from "@mui/icons-material/CallEnd";
import PhotoIcon from "@mui/icons-material/Photo";

import { useGetMeetingEvents } from "../../../store"
import { CalendarSlot, MeetingEvent, MeetingEventType, Organisation, User } from "../../../types"
import React from "react";

interface MeetingTimeLogProps {
    meeting: CalendarSlot
    userDictionary: Record<string, User>
    teamDictionary: Record<string, Organisation>
    selectedClientId?: string
}

const MeetingTimeLog: React.FC<MeetingTimeLogProps> = ({
    meeting,
    userDictionary,
    teamDictionary,
    selectedClientId,
}) => {

    const { data: events = [], isLoading } = useGetMeetingEvents([meeting.meetingId], selectedClientId)

    const getTeamName = (teamId: string | undefined) => {
        if (!teamId) {
            return "Unknown Team"
        }

        return teamDictionary[teamId]?.name || "Unknown Team"
    }

    const getUserName = (userId: string | undefined) => {
        if (!userId) {
            return "Unknown User"
        }

        return userDictionary[userId]?.fullName || "Unknown User"
    };

    const getJoinType = (event: MeetingEvent) => {
        if (!event.joinType) {
            return ""
        }

        return `(${event.joinType}) `
    }

    const formatTimeForLog = (date: Date | undefined) => {
        if (!date) {
            return "Unknown Time"
        }

        const parts = new Intl.DateTimeFormat("en-AU", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        }).formatToParts(date);

        return parts
            .map(part => {
                if (part.type === "literal" && part.value === ", ") {
                    return " ";
                }
                if (part.type === "dayPeriod") {
                    return part.value.toUpperCase();
                }
                return part.value;
            })
            .join("");
    };

    const getEventDisplay = (event: MeetingEvent): { icon: JSX.Element; description: string; textColor: string } => {
        switch (event.type) {
            case MeetingEventType.ScheduledStart:
                return {
                    icon: <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: "success.main" }} />,
                    description: "Scheduled start",
                    textColor: "success.main",
                };
            case MeetingEventType.MeetingStarted:
                return {
                    icon: <CircleIcon sx={{ fontSize: 16, color: "success.main" }} />,
                    description: "Actual start",
                    textColor: "success.main",
                };
            case MeetingEventType.AttendeeJoined:
                return {
                    icon: <CallIcon sx={{ fontSize: 16, color: "success.main" }} />,
                    description: `${getUserName(event.userId)} ${getJoinType(event)}joined`,
                    textColor: "success.main",
                };
            case MeetingEventType.AttendeeAdded:
                return {
                    icon: <PersonAddIcon sx={{ fontSize: 16, color: "info.main" }} />,
                    description: `${getUserName(event.userId)} added ${getUserName(event.id)}`,
                    textColor: "info.main",
                };
            case MeetingEventType.AttendeeRemoved:
                return {
                    icon: <PersonRemoveIcon sx={{ fontSize: 16, color: "error.main" }} />,
                    description: `${getUserName(event.userId)} removed ${getUserName(event.id)}`,
                    textColor: "error.main",
                };
            case MeetingEventType.OrganisationAdded:
                return {
                    icon: <SupervisorAccountIcon sx={{ fontSize: 16, color: "info.main" }} />,
                    description: `${getUserName(event.userId)} added team ${getTeamName(event.id)}`,
                    textColor: "info.main",
                };
            case MeetingEventType.OrganisationRemoved:
                return {
                    icon: <SupervisorAccountIcon sx={{ fontSize: 16, color: "error.main" }} />,
                    description: `${getUserName(event.userId)} removed team ${getTeamName(event.id)}`,
                    textColor: "error.main",
                };
            case MeetingEventType.ScheduledEnd:
                return {
                    icon: <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: "error.main" }} />,
                    description: "Scheduled end",
                    textColor: "error.main",
                };
            case MeetingEventType.AttendeeDropped:
            case MeetingEventType.AttendeeLeft:
                return {
                    icon: <CallEndIcon sx={{ fontSize: 16, color: "error.main" }} />,
                    description: `${getUserName(event.userId)} ${getJoinType(event)}left`,
                    textColor: "error.main",
                };
            case MeetingEventType.MeetingEnded:
                return {
                    icon: <CircleIcon sx={{ fontSize: 16, color: "error.main" }} />,
                    description: "Actual end",
                    textColor: "error.main",
                };
            case MeetingEventType.MeetingCreated:
                return {
                    icon: <AccessTimeIcon sx={{ fontSize: 16, color: "info.main" }} />,
                    description: `${getUserName(event.userId)} scheduled meeting at ${formatTimeForLog(event.dateField)}`,
                    textColor: "info.main",
                };
            case MeetingEventType.MeetingTime:
                return {
                    icon: <AccessTimeIcon sx={{ fontSize: 16, color: "info.main" }} />,
                    description: `${getUserName(event.userId)} changed meeting time to ${formatTimeForLog(event.dateField)}`,
                    textColor: "info.main",
                };
            case MeetingEventType.ProposedTime:
                return {
                    icon: <AccessTimeIcon sx={{ fontSize: 16, color: "info.main" }} />,
                    description: `${getUserName(event.userId)} proposed meeting times`,
                    textColor: "info.main",
                };
            case MeetingEventType.MeetingCancelled:
                return {
                    icon: <CancelIcon sx={{ fontSize: 16, color: "error.main" }} />,
                    description: `${getUserName(event.userId)} cancelled meeting`,
                    textColor: "error.main",
                };
            case MeetingEventType.SubstituteFailed:
                return {
                    icon: <PersonIcon sx={{ fontSize: 16, color: "error.main" }} />,
                    description: `${getUserName(event.userId)} failed to add via email link - ${event.id || "Unknown Error"}`,
                    textColor: "error.main",
                };
            case MeetingEventType.SubstituteAdded:
                return {
                    icon: <PersonAddIcon sx={{ fontSize: 16, color: "info.main" }} />,
                    description: `${getUserName(event.userId)} added ${getUserName(event.id)} via email link`,
                    textColor: "info.main",
                };
            case MeetingEventType.SubstituteLink:
                return {
                    icon: <PersonIcon sx={{ fontSize: 16, color: "text.disabled" }} />,
                    description: `${getUserName(event.userId)} requested a new substitute attendee link`,
                    textColor: "text.disabled",
                };
            case MeetingEventType.ConfirmAttendance: {
                const confirmed = event.id == "true";
                return {
                    icon: confirmed
                        ? <CheckCircleOutlinedIcon sx={{ fontSize: 16, color: "success.main" }} />
                        : <CancelOutlinedIcon sx={{ fontSize: 16, color: "error.main" }} />,
                    description: `${getUserName(event.userId)} ${confirmed ? "confirmed attendance" : "declined attendance"}`,
                    textColor: confirmed ? "success.main" : "error.main",
                };
            }
            case MeetingEventType.Upload:
                return {
                    icon: <PhotoIcon sx={{ fontSize: 16, color: "info.main" }} />,
                    description: `${getUserName(event.userId)} ${getJoinType(event)}uploaded ${event.id}`,
                    textColor: "info.main",
                };
            default:
                return {
                    icon: <CircleIcon sx={{ fontSize: 16, color: "text.disabled" }} />,
                    description: event.type,
                    textColor: "text.disabled",
                };
        }
    };

    return (
        <Stack>
            {isLoading &&
                <LinearProgress />
            }
            {!isLoading && events.length > 0 && [...events[0].events].reverse().map((event, index) => {
                const eventDisplay = getEventDisplay(event);

                return (
                    <React.Fragment key={`${event.type}${index}`}>
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{
                                py: 0.0,
                                px: 1,
                                borderTop: "1px solid #ccc",
                                "&:hover": {
                                    backgroundColor: "#FFF",
                                },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily: "monospace",
                                    color: "text.secondary",
                                }}
                            >
                                {formatTimeForLog(event.eventDate)}
                            </Typography>
                            <Stack
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {eventDisplay.icon}
                            </Stack>
                            <Typography variant="body2">
                                {eventDisplay.description}
                            </Typography>
                        </Stack>
                    </React.Fragment>
                )
            })}
            {!isLoading && events.length === 0 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        fontStyle: "italic",
                        textAlign: "center",
                        py: 2,
                    }}
                >
                    No timeline events available
                </Typography>
            )}
        </Stack>
    )
}

export default MeetingTimeLog